import { Component } from '@theme/component';
import { fetchConfig, isDesktopBreakpoint } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * Resolves the currently selected variant for a single bundle item (product image + compact
 * variant picker), purely client-side against the embedded variants JSON - no network round-trip.
 * Mirrors the matching logic used by QuickAddInlineComponent (assets/quick-add-inline.js).
 */
class BundleItem {
  /**
   * @param {HTMLElement} container - `.complete-the-look-bundle__item` element
   * @param {() => void} onChange - called whenever the resolved variant/price/availability changes
   */
  constructor(container, onChange) {
    this.container = container;
    this.onChange = onChange;
    this.variantIdInput = container.querySelector('[data-field="variant-id"]');
    this.variantPriceInput = container.querySelector('[data-field="variant-price"]');
    this.variantInputs = container.querySelectorAll('.complete-the-look-bundle__variant-input');
    this.loadVariantsData();

    this.variantInputs.forEach((input) => input.addEventListener('change', () => this.resolve()));
    this.resolve();
  }

  loadVariantsData() {
    const script = this.container.querySelector('[data-field="variants-data"]');
    if (!script) {
      this.productData = null;
      return;
    }
    try {
      this.productData = JSON.parse(script.textContent ?? '');
    } catch (error) {
      this.productData = null;
    }
  }

  getSelectedOptions() {
    const selected = {};
    this.container.querySelectorAll('.complete-the-look-bundle__variant-input:checked').forEach((input) => {
      selected[input.dataset.optionPosition] = input.value;
    });
    return selected;
  }

  hasSelectedAllOptions(selectedOptions) {
    if (!this.productData?.options) return false;
    return this.productData.options.every((_, index) => Boolean(selectedOptions[(index + 1).toString()]));
  }

  findMatchingVariant() {
    if (!this.productData?.variants) return null;
    const selectedOptions = this.getSelectedOptions();

    for (const variant of this.productData.variants) {
      const matches = this.productData.options.every((_, index) => {
        const selectedValue = selectedOptions[(index + 1).toString()];
        return !selectedValue || variant.options[index] === selectedValue;
      });

      if (matches && variant.available) return variant;
    }

    return null;
  }

  resolve() {
    if (this.productData) {
      const selectedOptions = this.getSelectedOptions();
      const variant = this.hasSelectedAllOptions(selectedOptions) ? this.findMatchingVariant() : null;
      this.variantId = variant ? variant.id.toString() : null;
      this.priceCents = variant ? variant.price : null;
      this.available = Boolean(variant);
    } else {
      const disabled = this.variantIdInput?.disabled ?? true;
      this.variantId = !disabled ? this.variantIdInput?.value || null : null;
      this.priceCents = this.variantPriceInput ? Number(this.variantPriceInput.value) : null;
      this.available = !disabled && Boolean(this.variantId);
    }

    if (this.variantIdInput && this.variantId) {
      this.variantIdInput.value = this.variantId;
    }

    this.onChange();
  }
}

/**
 * Bundles the current product with one or more complementary products ("complete the look") and
 * adds all of them to the cart with a single click, via the /cart/add.js multi-item `items` payload.
 *
 * @typedef {object} CompleteTheLookBundleRefs
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} totalPrice
 * @extends Component<CompleteTheLookBundleRefs>
 */
class CompleteTheLookBundleComponent extends Component {
  requiredRefs = ['addToCartButton', 'totalPrice'];

  /**
   * Image link that should skip its next navigation because a mobile tap just revealed its size
   * picker instead. @type {HTMLAnchorElement | null}
   */
  #pendingSkipNavigationLink = null;

  connectedCallback() {
    super.connectedCallback();

    const itemElements = this.querySelectorAll('.complete-the-look-bundle__item');
    this.items = Array.from(itemElements).map((element) => new BundleItem(element, () => this.updateState()));
    this.updateState();

    this.addEventListener('pointerdown', this.#handleMobileSizeReveal);
    this.addEventListener('click', this.#handleImageLinkClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#handleMobileSizeReveal);
    this.removeEventListener('click', this.#handleImageLinkClick);
  }

  /**
   * Reveals an item's size picker on the first mobile tap on its image, matching the
   * quick-add-inline behavior on product cards. The tap is consumed instead of navigating.
   * @param {PointerEvent} event
   */
  #handleMobileSizeReveal = (event) => {
    if (isDesktopBreakpoint() || event.pointerType === 'mouse') return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('.complete-the-look-bundle__image-link');
    if (!link || !(link instanceof HTMLAnchorElement)) return;

    const item = link.closest('.complete-the-look-bundle__item');
    if (!item || !item.querySelector('.complete-the-look-bundle__variants')) return;
    if (item.hasAttribute('data-mobile-size-visible')) return;

    this.querySelectorAll('.complete-the-look-bundle__item[data-mobile-size-visible]').forEach((el) => {
      if (el !== item) el.removeAttribute('data-mobile-size-visible');
    });

    item.setAttribute('data-mobile-size-visible', '');
    this.#pendingSkipNavigationLink = link;
  };

  /**
   * Prevents navigation for the image link tap that just revealed its size picker.
   * @param {MouseEvent} event
   */
  #handleImageLinkClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('.complete-the-look-bundle__image-link') : null;

    if (link && link === this.#pendingSkipNavigationLink) {
      event.preventDefault();
    }

    this.#pendingSkipNavigationLink = null;
  };

  updateState() {
    if (!this.items) return;

    const allAvailable = this.items.every((item) => item.available);

    this.refs.addToCartButton.disabled = !allAvailable;

    if (allAvailable) {
      const totalCents = this.items.reduce((sum, item) => sum + (item.priceCents ?? 0), 0);
      this.refs.totalPrice.textContent = this.formatMoney(totalCents);
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;

    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency,
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  /**
   * Handles a direct click on the bundle's "Add to bag" button: adds every resolved item variant
   * to the cart in one request instead of one add per product. Only ever runs from that button's
   * own click - it is not wired to any form submit event.
   * @param {Event} event
   */
  handleAdd(event) {
    if (this.refs.addToCartButton.disabled) return;
    if (!this.items?.every((item) => item.available)) return;

    const items = this.items.map((item) => ({ id: Number(item.variantId), quantity: 1 }));

    this.refs.addToCartButton.disabled = true;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        sectionIds.push(element.dataset.sectionId);
      }
    });

    const fetchCfg = fetchConfig('json', {
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });

    fetch(Theme.routes.cart_add_url, fetchCfg)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(new CartErrorEvent(this.id, response.message, response.description, response.errors));
          return;
        }

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'product-form-component',
            itemCount: items.length,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.updateState();
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }
}

if (!customElements.get('complete-the-look-bundle')) {
  customElements.define('complete-the-look-bundle', CompleteTheLookBundleComponent);
}
